import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Typography,
  Spin,
  Modal,
  InputNumber,
  Upload,
  List,
  Tag,
  Space,
  Divider
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined,
  PictureOutlined,
  FileOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { announcementService } from '../../services/announcementService';
import { useAuth } from '../../contexts/AuthContext';
import { Announcement, CreateAnnouncementRequest, UpdateAnnouncementRequest, Attachment } from '../../types';
import { applyStyle, removeStyle, insertImage } from '../../utils/styleUtils';
import { insertTable } from '../../utils/tableUtils';
import { attachmentService } from '../../services/attachmentService';
import dayjs from 'dayjs';
import '../../styles/contentStyles.css';

const { Title, Text } = Typography;

const AnnouncementEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const isCreate = id === 'create';
  const [form] = Form.useForm();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = !!id && !isCreate;
  const contentRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef<string>('');
  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [tableRows, setTableRows] = useState(2);
  const [tableCols, setTableCols] = useState(2);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');

  // 첨부파일 관련 상태
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);

  const fetchAnnouncement = useCallback(async () => {
    if (!id || isCreate) return;
    
    setLoading(true);
    try {
      const data = await announcementService.getAnnouncement(id);
      setAnnouncement(data);
      
      // 첨부파일도 함께 조회
      try {
        const attachmentData = await attachmentService.getAnnouncementAttachments(id);
        setAttachments(attachmentData);
      } catch (error) {
        setAttachments(data.attachments || []);
      }
      
      form.setFieldsValue({
        title: data.title,
        content: data.content,
      });

      // Form 설정 후 contentEditable div 초기화
      setTimeout(() => {
        if (contentRef.current && data.content) {
          contentRef.current.innerHTML = data.content;
          lastContentRef.current = data.content;
        }
      }, 0);
    } catch (error: any) {
      message.error(t('error.announcementLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, isCreate, form, t]);

  useEffect(() => {
    if (isEdit) {
      fetchAnnouncement();
    } else if (isCreate) {
      // 생성 모드: 빈 내용으로 초기화
      if (contentRef.current) {
        contentRef.current.innerHTML = '';
        lastContentRef.current = '';
      }
    }
  }, [id, isEdit, t, isCreate, fetchAnnouncement]);

  // 권한 확인
  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('common.noPermission')}</Title>
        <Button type="primary" onClick={() => navigate('/announcements')}>
          {t('button.backToList')}
        </Button>
      </div>
    );
  }

  // 승인된 공지사항 수정 방지
  if (announcement?.is_approved) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('announcements.approvedCannotEdit')}</Title>
        <Button type="primary" onClick={() => navigate('/announcements')}>
          {t('button.backToList')}
        </Button>
      </div>
    );
  }

  const handleSave = async (values: CreateAnnouncementRequest | UpdateAnnouncementRequest) => {
    setSaving(true);
    try {
      if (isEdit && announcement) {
        await announcementService.updateAnnouncement(announcement.announcement_id, values as UpdateAnnouncementRequest);
        message.success(t('success.announcementUpdate'));
      } else {
        await announcementService.createAnnouncement(values as CreateAnnouncementRequest);
        message.success(t('success.announcementCreate'));
      }
      navigate('/announcements');
    } catch (error: any) {
      message.error(error.response?.data?.message || t('error.announcementSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/announcements');
  };

  // 표 삽입 함수
  const handleInsertTable = () => {
    const updatedContent = insertTable(contentRef, tableRows, tableCols);
    // 즉시 폼 값 업데이트
    form.setFieldValue('content', updatedContent);
    lastContentRef.current = updatedContent;
    setTableModalVisible(false);
  };

  // 이미지 삽입 함수
  const handleInsertImage = async () => {
    if (!imageUrl.trim() && !imageFile) {
      message.error(t('error.imageUrlOrFile'));
      return;
    }

    // 이미지 파일이 있는 경우 서버에 업로드
    if (imageFile) {
      try {
        if (!announcement?.announcement_id) {
          message.error(t('error.saveAnnouncementFirst'));
          return;
        }

        const uploadedImage = await attachmentService.uploadEditorImage(
          imageFile, 
          'announcement', 
          announcement.announcement_id
        );
        
        // 업로드된 이미지 URL을 사용하여 삽입
        insertImage(uploadedImage.file_path, form, contentRef);
        message.success(t('success.imageUpload'));
      } catch (error: any) {
        message.error(error.response?.data?.message || t('error.imageUpload'));
        return;
      }
    } else {
      // URL 이미지 삽입
      insertImage(imageUrl, form, contentRef);
    }
    
    setImageModalVisible(false);
    setImageUrl('');
    setImageFile(null);
    setImagePreview('');
  };

  // 첨부파일 업로드 핸들러
  const handleFileUpload = async (file: File) => {
    if (!announcement) {
      message.error(t('error.saveAnnouncementFirst'));
      return;
    }

    setUploading(true);
    try {
      await attachmentService.uploadAttachment({
        file,
        announcementId: announcement.announcement_id
      });
      message.success('첨부파일이 업로드되었습니다.');
      
      // 첨부파일 목록 새로고침
      const attachmentData = await attachmentService.getAnnouncementAttachments(announcement.announcement_id);
      setAttachments(attachmentData);
    } catch (error: any) {
      message.error(error.response?.data?.message || '첨부파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  // 첨부파일 삭제 핸들러
  const handleFileDelete = async (attachment: Attachment) => {
    try {
      await attachmentService.deleteAttachment(attachment.attachment_id);
      message.success('첨부파일이 삭제되었습니다.');
      
      // 첨부파일 목록 새로고침
      if (announcement) {
        const attachmentData = await attachmentService.getAnnouncementAttachments(announcement.announcement_id);
        setAttachments(attachmentData);
      }
    } catch (error: any) {
      message.error('첨부파일 삭제에 실패했습니다.');
    }
  };

  // 첨부파일 다운로드 핸들러
  const handleFileDownload = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, attachment.file_name);
    } catch (error: any) {
      message.error('첨부파일 다운로드에 실패했습니다.');
    }
  };

  // 파일 크기 포맷팅
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('loading.announcementLoad')}</div>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ marginRight: 16 }}
          >
            {t('button.cancel')}
          </Button>
          
                      <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={() => {
              // contentEditable div의 내용을 폼에 반영
              if (contentRef.current) {
                const content = contentRef.current.innerHTML;
                form.setFieldValue('content', content);
                lastContentRef.current = content;
              }
              form.submit();
            }}
            loading={saving}
          >
            {t('button.save')}
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="title"
            label={t('form.title')}
            rules={[{ required: true, message: t('validation.title') }]}
          >
            <Input placeholder={t('input.title')} />
          </Form.Item>

          <Form.Item
            name="content"
            label={t('form.content')}
            rules={[{ required: true, message: t('validation.content') }]}
          >
            {/* 스타일 툴바 - 내용 영역 바로 위에 위치 */}
            <div style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderBottom: 'none',
              borderRadius: '6px 6px 0 0',
              padding: '12px',
              marginBottom: 0,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {/* 텍스트 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('진하게', form, contentRef)}
              title={t('editor.bold')}
            >
              {t('editor.bold')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('왼쪽', form, contentRef)}
              title={t('editor.alignLeft')}
            >
              {t('editor.alignLeft')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('가운데', form, contentRef)}
              title={t('editor.alignCenter')}
            >
              {t('editor.alignCenter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('오른쪽', form, contentRef)}
              title={t('editor.alignRight')}
            >
              {t('editor.alignRight')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
            
            {/* 법률 문서 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('제목', form, contentRef)}
              title={t('editor.titleStyle')}
            >
              {t('editor.title')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('장', form, contentRef)}
              title={t('editor.chapterStyle')}
            >
              {t('editor.chapter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('조', form, contentRef)}
              title={t('editor.articleStyle')}
            >
              {t('editor.article')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('항', form, contentRef)}
              title={t('editor.paragraphStyle')}
            >
              {t('editor.paragraph')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('호', form, contentRef)}
              title={t('editor.subParagraphStyle')}
            >
              {t('editor.subParagraph')}
            </Button>
            <Button 
              size="small" 
              danger
              onClick={() => removeStyle(form, contentRef)}
              title={t('editor.removeStyle')}
            >
              {t('editor.removeStyle')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
            
            {/* 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => setTableModalVisible(true)}
              title={t('editor.insertTable')}
            >
              {t('editor.table')}
            </Button>
            {/* 개정사항 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('개정사항', form, contentRef)}
              title={t('editor.amendmentTable')}
            >
              {t('editor.amendment')}
            </Button>
            {/* 이미지 삽입 버튼 */}
            <Button
              size="small"
              icon={<PictureOutlined />}
              onClick={() => setImageModalVisible(true)}
              title={t('editor.insertImage')}
            >
              {t('editor.image')}
            </Button>
          </div>


            <div
              ref={contentRef}
              contentEditable={true}
              className="editor-content"
              style={{
                height: '600px',
                maxHeight: '600px',
                border: '1px solid #d9d9d9',
                borderTop: 'none',
                borderRadius: '0 0 6px 6px',
                padding: '16px',
                fontFamily: 'monospace',
                fontSize: '16px',
                lineHeight: '1.6',
                outline: 'none',
                overflowY: 'auto',
                backgroundColor: '#ffffff'
              }}
              onInput={(e) => {
                const target = e.target as HTMLDivElement;
                lastContentRef.current = target.innerHTML;
              }}
              onBlur={(e) => {
                const target = e.target as HTMLDivElement;
                const newContent = target.innerHTML;
                if (newContent !== lastContentRef.current) {
                  lastContentRef.current = newContent;
                  // onBlur에서는 폼 값을 업데이트하지 않음 (저장 버튼에서만 업데이트)
                }
              }}
            />
          </Form.Item>

          {/* 첨부파일 섹션 */}
          <Divider orientation="left">{t('form.attachments')}</Divider>
          
          <div style={{ marginBottom: 16 }}>
            <Upload
              beforeUpload={(file) => {
                handleFileUpload(file);
                return false; // 자동 업로드 방지
              }}
              showUploadList={false}
              disabled={!announcement || uploading}
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={uploading}
                disabled={!announcement}
              >
                {t('button.uploadAttachment')}
              </Button>
            </Upload>
          </div>

          {/* 첨부파일 목록 */}
          <div style={{ marginBottom: 16 }}>
            {attachments.length > 0 && (
              <List
                size="small"
                dataSource={attachments}
                renderItem={(attachment) => (
                  <List.Item
                    actions={[
                      <Button
                        key="download"
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleFileDownload(attachment)}
                        title={t('button.download')}
                      />,
                      <Button
                        key="delete"
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(attachment)}
                        title={t('button.delete')}
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileOutlined style={{ fontSize: '16px', color: '#1890ff' }} />}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '13px' }}>
                            {attachment.file_name}
                          </span>
                          <Tag color="blue">{formatFileSize(attachment.file_size)}</Tag>
                        </div>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {dayjs(attachment.uploaded_at).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            {attachments.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#8c8c8c',
                border: '1px dashed #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}>
                <FileOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                <div>{t('announcements.form.noAttachments')}</div>
              </div>
            )}
          </div>
        </Form>
      </Card>

      {/* 표 삽입 모달 */}
      <Modal
        title={t('editor.insertTable')}
        open={tableModalVisible}
        onOk={handleInsertTable}
        onCancel={() => setTableModalVisible(false)}
        okText={t('editor.insert')}
        cancelText={t('common.cancel')}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.rowCount')}:</label>
          <InputNumber
            min={1}
            max={20}
            value={tableRows}
            onChange={(value) => setTableRows(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.columnCount')}:</label>
          <InputNumber
            min={1}
            max={10}
            value={tableCols}
            onChange={(value) => setTableCols(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          {t('editor.tableInsertNote')}
        </div>
      </Modal>

      {/* 이미지 삽입 모달 */}
      <Modal
        title={t('editor.insertImage')}
        open={imageModalVisible}
        onOk={handleInsertImage}
        onCancel={() => setImageModalVisible(false)}
        okText={t('editor.insert')}
        cancelText={t('common.cancel')}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.imageUrl')}:</label>
          <Input
            placeholder={t('editor.imageUrlPlaceholder')}
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.imageFile')}:</label>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setImageFile(file);
                const reader = new FileReader();
                reader.onload = (e) => {
                  setImagePreview(e.target?.result as string);
                };
                reader.readAsDataURL(file);
              }
            }}
          />
        </div>
        {(imagePreview || imageUrl) && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.preview')}:</label>
            <img 
              src={imagePreview || imageUrl} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #d9d9d9' }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AnnouncementEdit; 